import { Slider, XStack, YStack } from "tamagui";

import type { AskUserQuestionScale } from "@/api/generated/models";
import { Button, Paragraph } from "@/components";
import SkipOrSubmit from "@/conversation/AskUserQuestionsPanel/SkipOrSubmit";

export default function ScaleQuestion({
  question,
  value,
  onChange,
  onSubmit,
  onSkip,
  isLast,
}: {
  question: AskUserQuestionScale;
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  onSkip: () => void;
  isLast: boolean;
}) {
  const min = question.scale_range.min;
  const max = question.scale_range.max;
  const labelMin = question.scale_range.label_min || min.toString();
  const labelMax = question.scale_range.label_max || max.toString();
  const useButtons = max - min <= 10;
  const values = Array.from({ length: max - min + 1 }, (_, i) => min + i);
  const current = value ? Number(value) : min;

  return (
    <>
      {useButtons ? (
        <XStack gap="$100" paddingHorizontal="$150" paddingTop="$100">
          {values.map((v) => {
            const selected = value === String(v);
            return (
              <Button
                dataTrackingId="askUserQuestionOption"
                key={v}
                mode={selected ? "primary" : "secondary"}
                flex={1}
                flexBasis={0}
                borderWidth="$strokeWidth"
                hoverStyle={{
                  borderColor: selected
                    ? "$fillButtonPrimary"
                    : "$strokeButtonSecondary",
                }}
                borderColor={
                  selected ? "$fillButtonPrimary" : "$strokeButtonSecondary"
                }
                paddingHorizontal="$50"
                onPress={() => {
                  onChange(String(v));
                  onSubmit();
                }}
              >
                {v.toString()}
              </Button>
            );
          })}
        </XStack>
      ) : (
        <YStack gap="$150" paddingHorizontal="$150" paddingVertical="$100">
          <Slider
            min={min}
            max={max}
            step={1}
            value={[current]}
            onValueChange={([v]) => onChange(String(v))}
          >
            <Slider.Track backgroundColor="$strokeSubdued">
              <Slider.TrackActive backgroundColor="$fillButtonPrimary" />
            </Slider.Track>
            <Slider.Thumb
              circular
              size="$250"
              borderWidth={0}
              backgroundColor="$fillButtonPrimary"
              hoverStyle={{ backgroundColor: "$fillButtonPrimary" }}
              focusVisibleStyle={{ backgroundColor: "$fillButtonPrimary" }}
              focusStyle={{ backgroundColor: "$fillButtonPrimary" }}
              pressStyle={{ backgroundColor: "$fillButtonPrimary" }}
            />
          </Slider>
          <XStack justifyContent="space-between">
            <Paragraph size="$sm" color="$textSecondary">
              {labelMin}
            </Paragraph>
            <Paragraph size="$sm" color="$textSecondary">
              {labelMax}
            </Paragraph>
          </XStack>
        </YStack>
      )}
      <XStack
        justifyContent="flex-end"
        paddingHorizontal="$150"
        marginTop="$250"
        paddingBottom="$150"
      >
        <SkipOrSubmit
          showSubmit={!useButtons}
          submitDisabled={!value}
          isLast={isLast}
          onSkip={onSkip}
          onSubmit={onSubmit}
        />
      </XStack>
    </>
  );
}
